import { Injectable } from "@angular/core";
import { forkJoin, Observable, of as observableOf } from "rxjs";
import { catchError, map, skip, take, tap } from "rxjs/operators";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { FiatService } from "@main-services/currency/fiat.service";
import { CryptocoinService } from "@main-services/currency/cryptocoin.service";
import { MetalService } from "@main-services/currency/metal.service";
import { IndexService } from "@main-services/currency/index.service";
import { StockService } from "@main-services/currency/stock.service";
import { EtfService } from "@main-services/currency/etf.service";
import { Cryptocoin, Fiat } from "@models";
import { Metal } from "@models/asset/metal/metal";
import { Index } from "@models/asset/index/index.model";
import { Stock } from "@models/derivative/currencies/stock";
import { Etf } from "@models/derivative/currencies/etf";
import { Helper, parseList } from "@helpers/functions";
import { InfoService } from "@core-services/info.service";
import { ServiceNames } from "@models/service_enums";
import { InitService } from "@core-services/init.service";
import { AssetSettings } from "@models/tradable/split/asset_settings";
import { AssetPrice } from "@models/tradable/split/asset_price";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TranslationService } from "@core-services/translation.service";
import { EtcService } from "@main-services/currency/etc.service";
import { Etc } from "@models/derivative/currencies/etc";
import { Currency } from "@models/interfaces/currency";
import { LeveragedTokenService } from "@main-services/currency/leveragedToken.service";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { FiatEarnService } from "@main-services/currency/fiat_earn.service";
import { FiatEarn } from "@models/derivative/currencies/fiat_earn";
import { SecurityTokenService } from "@main-services/currency/securityToken.service";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";
import { BFFSecurityAsset } from "@models/asset/bff_security_asset";
import { Tradable } from "@models/interfaces/tradable";

// TODO: eslint fix
/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
	providedIn: "root"
})
export class InitAssetsService {
	public name: string = ServiceNames.INIT_ASSETS_SERVICE;

	public constructor(
		private apiCallService: ApicallService,
		private cryptocoinService: CryptocoinService,
		private metalService: MetalService,
		private indexService: IndexService,
		private stockService: StockService,
		private etfService: EtfService,
		private etcService: EtcService,
		private leveragedTokenService: LeveragedTokenService,
		private fiatService: FiatService,
		private fiatEarnService: FiatEarnService,
		private infoService: InfoService,
		private initService: InitService,
		private tradableListService: TradableListService,
		private translationService: TranslationService,
		private securityTokenService: SecurityTokenService
	) {}

	public initialize(): Observable<boolean> {
		if (!this.initService.didInit([this.name])) {
			/*
				Wait for the entire data from endpoints (fiats, static tradables, low-volatile, high-volatile)
			 */
			return forkJoin([this.fetchFiats$(), this.fetchAllTradables()]).pipe(
				map((results) => {
					const finished = results.every((c: boolean) => !!c);
					if (finished) {
						this.initService.init(this.name);
					}
					return finished;
				}),
				tap(() => this.handleLanguageChanges())
			);
		}
		return observableOf(false);
	}

	private handleLanguageChanges(): void {
		this.translationService
			.languageUpdate$()
			.pipe(skip(1))
			.subscribe(() => {
				this.fetchFiats$().pipe(take(1)).subscribe();
			});
	}

	/*
		Fiat currencies
	 */
	private fetchFiats$(): Observable<boolean> {
		return this.apiCallService.get("currencies/fiats", new ApicallOption(Fiat)).pipe(
			tap((response: Fiat[]) => (this.fiatService.list = response)),
			map(() => true),
			catchError((error) => {
				Helper.handleError(error);
				return observableOf(false);
			})
		);
	}

	/*
		Fetches Static tradables list and adds low-volatile and high-volatile on top of it.
	 */

	public fetchAllTradables(): Observable<boolean> {
		return forkJoin([
			this.fetchStaticTradables(),
			this.fetchLowVolatileTradables(),
			this.fetchHighVolatileTradables()
		]).pipe(
			// Return true if all loaded successfully:
			map((results: any[]) => this.parseAllResponses(results)),
			catchError((error) => {
				Helper.handleError(error);
				return observableOf(false);
			})
		);
	}

	/*
		Fetches the Tradables containing the [Static] fields.
		Uses ETag caching (if-none-match header) by default.
	 */

	private fetchStaticTradables(): Observable<Currency[]> {
		let o = new ApicallOption();
		o.apiVersion = "v3";

		// Changes needed for UK
		const stealcoin_enabled = false;
		return this.apiCallService.get("currencies", o).pipe(
			map((response) => {
				if (stealcoin_enabled) {
					return response["attributes"];
				}

				const { ...other_attributes } = response["attributes"];

				return other_attributes;
			}),
			catchError((error) => {
				Helper.handleError(error);
				return observableOf([]);
			})
		);
	}

	/*
		Fetches the [Low-volatile] fields-only for each Tradable.
		Uses ETag caching (if-none-match header) by default.
	 */

	private fetchLowVolatileTradables(): Observable<AssetSettings[]> {
		return this.apiCallService.get("assets/settings", new ApicallOption(AssetSettings)).pipe(
			map((list: AssetSettings[]) => list),
			catchError((error) => {
				Helper.handleError(error);
				return observableOf([]);
			})
		);
	}

	/*
		Fetches the [High-volatile] fields-only for each Tradable.
		Does NOT use ETag caching.
	 */
	private fetchHighVolatileTradables(): Observable<AssetPrice[]> {
		return this.apiCallService.post("assets/prices", new ApicallOption(AssetPrice)).pipe(
			map((list: AssetPrice[]) => list),
			catchError((error) => {
				Helper.handleError(error);
				return observableOf([]);
			})
		);
	}

	/*
		Parsing and adding all static, low-volatile, high-volatile data at once
	 */
	private parseAllResponses(res: any[]): boolean {
		// Assets lists / Static data
		if (!res[0] || res[0].length === 0) {
			return false;
		}

		const assetData: any[] = res[0];
		const coins: Cryptocoin[] = parseList(assetData["cryptocoins"], Cryptocoin);
		const commodities: Metal[] = parseList(assetData["commodities"], Metal);
		const indexes: Index[] = parseList(assetData["index"], Index);
		const stocks: Stock[] = parseList(assetData["stocks"], Stock);
		const etfs: Etf[] = parseList(assetData["etfs"], Etf);
		const etcs: Etc[] = parseList(assetData["etcs"], Etc);
		const leveragedTokens: LeveragedToken[] = assetData["leveraged_tokens"]
			? parseList(assetData["leveraged_tokens"], LeveragedToken)
			: [];
		const fiatEarn: FiatEarn[] = assetData["fiat_earns"] ? parseList(assetData["fiat_earns"], FiatEarn) : [];
		const securityTokens: SecurityToken[] = assetData["security_tokens"]
			? parseList(assetData["security_tokens"], SecurityToken)
			: [];

		const tradableList = [
			coins,
			commodities,
			indexes,
			stocks,
			etfs,
			etcs,
			leveragedTokens,
			fiatEarn,
			securityTokens
		];

		// Low volatile / High volatile data
		if (!res[1] || !res[2] || res[1].length === 0 || res[2].length === 0) {
			return false;
		}
		const assetSettings: AssetSettings[] = res[1];
		const assetPrices: AssetPrice[] = res[2];
		tradableList.forEach((list) => {
			list.forEach((item) => {
				const settings: AssetSettings = assetSettings.find((s) => s.getId() === item.getId());
				const prices: AssetPrice = assetPrices.find((p) => p.getId() === item.getId());
				if (settings) {
					item.setAssetSettings(settings);
				}
				if (prices) {
					item.setAssetPrices(prices);
				}
			});
		});

		const hasSettingsAndPrices = (item: Tradable): boolean => !!item.getAssetSettings() && !!item.getAssetPrices();

		// Populating services
		this.cryptocoinService.list = coins.filter(hasSettingsAndPrices);
		this.metalService.list = commodities.filter(hasSettingsAndPrices);
		this.indexService.list = indexes.filter(hasSettingsAndPrices);
		this.stockService.list = stocks.filter(hasSettingsAndPrices);
		this.etfService.list = etfs.filter(hasSettingsAndPrices);
		this.etcService.list = etcs.filter(hasSettingsAndPrices);
		this.leveragedTokenService.list = leveragedTokens.filter(hasSettingsAndPrices);
		this.fiatEarnService.list = fiatEarn.filter(hasSettingsAndPrices);
		this.securityTokenService.list = securityTokens.filter(hasSettingsAndPrices);
		return true;
	}

	public addSecurityAsset(asset: BFFSecurityAsset): void {
		if (asset.isStock()) {
			this.stockService.upsert(asset);
		} else if (asset.isEtf) {
			this.etfService.upsert(asset);
		} else if (asset.isEtc) {
			this.etcService.upsert(asset);
		}
	}
}
